import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon, LockIcon, PlusIcon, TrashIcon } from "vega-icons";

import {
  getGetNrlItemsQueryKey,
  useDeleteNrlItem,
  useGetNrlItems,
  useUpdateNrlItem,
} from "@vega-api/generated/coinvest-deal-opportunity-nrl/coinvest-deal-opportunity-nrl";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { NdaTrackingStatus } from "@vega-api/generated/types/ndaTrackingStatus";
import { type NrlItem } from "@vega-api/generated/types/nrlItem";
import { type NrlSide } from "@vega-api/generated/types/nrlSide";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { TrackingTag } from "../../TrackingTable/components/TrackingTag";

import { NrlSideToggle } from "./NrlSideToggle";
import { NrlTrackNewSearch } from "./NrlTrackNewSearch";

type NrlCellProps = {
  row: DealTrackingLimitedPartnerBreakdown;
  dealId: string;
  canEdit: boolean;
};

const NrlItemRow = ({
  item,
  dealOpportunityId,
  canEdit,
  onChanged,
}: {
  item: NrlItem;
  dealOpportunityId: string;
  canEdit: boolean;
  onChanged: () => void;
}) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const { mutate: updateNrlItem, isPending: isUpdating } = useUpdateNrlItem();
  const { mutate: deleteNrlItem, isPending: isDeleting } = useDeleteNrlItem();

  const handleUpdate = (data: { side?: NrlSide; isSigned?: boolean }) => {
    updateNrlItem(
      { dealOpportunityId, nrlItemId: item.id, data },
      {
        onSuccess: () => {
          onChanged();
          showToast({
            variant: "success",
            title: m.coInvestments_nrl_updateSuccess(),
          });
        },
        onError: (error) =>
          showErrorToast(error, {
            defaultMessage: m.coInvestments_nrl_updateError(),
          }),
      },
    );
  };

  const handleSideChange = (side: NrlSide) => {
    if (side === item.side) return;

    handleUpdate({ side });
  };

  const handleSignedToggle = () => handleUpdate({ isSigned: !item.isSigned });

  const handleDelete = () => {
    deleteNrlItem(
      { dealOpportunityId, nrlItemId: item.id },
      {
        onSuccess: () => {
          onChanged();
          showToast({
            variant: "success",
            title: m.coInvestments_nrl_deleteSuccess(),
          });
        },
        onError: (error) =>
          showErrorToast(error, {
            defaultMessage: m.coInvestments_nrl_deleteError(),
          }),
      },
    );
  };

  const isBusy = isUpdating || isDeleting;

  return (
    <div
      className="flex items-center gap-2 py-2"
      data-testid={`nrl-item-${item.id}`}
    >
      <span className="body-base-regular text-text/primary flex-1 min-w-0">
        {item.documentName}
      </span>
      <NrlSideToggle
        disabled={!canEdit || isBusy}
        onChange={handleSideChange}
        value={item.side}
      />
      <button
        aria-label={m.coInvestments_nrl_signedToggle()}
        aria-pressed={item.isSigned}
        className="group/nrl-signed shrink-0 disabled:opacity-50 rounded-default focus-outline-themed"
        data-testid={`nrl-item-signed-${item.id}`}
        disabled={!canEdit || isBusy}
        onClick={handleSignedToggle}
        type="button"
      >
        <CheckCircleIcon
          className={cnTwMerge(
            "size-4 transition-colors",
            item.isSigned
              ? "text-text/positive"
              : "text-text/secondary group-hover/nrl-signed:text-text/positive",
          )}
        />
      </button>
      <button
        aria-label={m.btn_delete()}
        className="group/nrl-delete shrink-0 disabled:opacity-50 rounded-default focus-outline-themed"
        data-testid={`nrl-item-delete-${item.id}`}
        disabled={!canEdit || isBusy}
        onClick={handleDelete}
        type="button"
      >
        <TrashIcon className="size-4 text-text/secondary transition-colors group-hover/nrl-delete:text-text/negative" />
      </button>
    </div>
  );
};

export const NrlCell = ({ row, dealId, canEdit }: NrlCellProps) => {
  const showToast = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setOpen] = useState(false);
  const [isSearchStep, setSearchStep] = useState(false);

  const dealOpportunityId = row.dealOpportunityId;
  const locked = row.ndaTrackingStatus !== NdaTrackingStatus.SIGNED;

  const {
    data: nrlItems = [],
    isFetching: isFetchingNrlItems,
    isPending: isLoadingNrlItems,
    isError: isNrlItemsError,
  } = useGetNrlItems(dealOpportunityId, {
    query: {
      enabled: isOpen && !locked,
      select: (response) => response.data,
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetNrlItemsQueryKey(dealOpportunityId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealTrackingForGpQueryKey(dealId),
    });
  };

  // Reset to the list step whenever the popover closes.
  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    if (!open) setSearchStep(false);
  };

  const handleTracked = () => {
    invalidate();
    setSearchStep(false);
    showToast({
      variant: "success",
      title: m.coInvestments_nrl_addSuccess(),
    });
  };

  const signedCount = row.numberOfNrlSigned ?? 0;
  const trackedCount = row.numberOfNrlTracked ?? 0;
  const allSigned = trackedCount > 0 && signedCount === trackedCount;

  const badge = (() => {
    if (trackedCount === 0) {
      return { variant: "outline" as const, className: undefined };
    }

    return allSigned
      ? { variant: "positive" as const, className: "text-text/positive" }
      : { variant: "pending" as const, className: "text-text/pending" };
  })();

  const nrlStatusMessage = (() => {
    if (isLoadingNrlItems) return m.common_loading();

    if (isNrlItemsError) return m.coInvestments_nrl_loadError();

    if (nrlItems.length === 0) return m.coInvestments_nrl_noTrackedLetters();

    return null;
  })();

  const nrlListContent = nrlStatusMessage ? (
    <p className="body-base-regular text-text/secondary py-2">
      {nrlStatusMessage}
    </p>
  ) : (
    nrlItems.map((item) => (
      <NrlItemRow
        canEdit={canEdit}
        dealOpportunityId={dealOpportunityId}
        item={item}
        key={item.id}
        onChanged={invalidate}
      />
    ))
  );

  if (locked) {
    return (
      <div
        className="flex items-center h-full"
        data-testid={`nrl-cell-${dealOpportunityId}`}
      >
        <TrackingTag
          dataTestId={`nrl-cell-locked-${dealOpportunityId}`}
          variant="disabled"
        >
          <LockIcon className="size-3" />
          {`${signedCount}/${trackedCount}`}
        </TrackingTag>
      </div>
    );
  }

  return (
    <div
      className="flex items-center h-full"
      data-testid={`nrl-cell-${dealOpportunityId}`}
    >
      <Popover onOpenChange={handleOpenChange} open={isOpen}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center h-full w-full focus-outline-themed"
            data-testid={`nrl-cell-trigger-${dealOpportunityId}`}
            type="button"
          >
            <TrackingTag className={badge.className} variant={badge.variant}>
              {`${signedCount}/${trackedCount}`}
            </TrackingTag>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96 p-3">
          {isSearchStep ? (
            <NrlTrackNewSearch
              dealId={dealId}
              dealOpportunityId={dealOpportunityId}
              onBack={() => setSearchStep(false)}
              onTracked={handleTracked}
              trackedDocumentIds={nrlItems.map(
                (item) => item.dataRoomDocumentId,
              )}
            />
          ) : (
            <>
              <div className="flex flex-col divide-y divide-border/primary">
                {nrlListContent}
              </div>
              {canEdit ? (
                <div className="pt-3 mt-1 border-t border-border/primary">
                  <ThemedButton
                    dataTestId="nrl-track-new-trigger"
                    disabled={isFetchingNrlItems}
                    fullWidth
                    icon={<PlusIcon className="size-4 shrink-0" />}
                    onClick={() => setSearchStep(true)}
                    variant="tertiary"
                  >
                    {m.coInvestments_nrl_trackNew()}
                  </ThemedButton>
                </div>
              ) : null}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
